import { useApi } from "../api";
import { createAdminCrudApi } from "../createAdminCrudApi";
import {
  GammaMValue,
  InsertLoadDurationClassBody,
  InsertServiceClassBody,
  SearchGammaMValuesResponse,
  SearchKmodValuesResponse,
  SearchLoadDurationClassesResponse,
  SearchServiceClassesResponse,
  UpdateGammaMValueBody,
  UpdateLoadDurationClassBody,
  UpdateServiceClassBody,
  UpsertKmodValueBody,
} from "./types/normativeTables";
import { SCREW_CONNECTOR_ADMIN_URL } from "./screwConnectorAdminUrl";
import { SCREW_CONNECTOR_URL } from "./screwConnectorUrl";

// The calculator reads every normative table too.

// ─── Load duration classes: keyed by code ───
export const useScrewConnectorLoadDurationClassesApi = () => {
  const crud = createAdminCrudApi<
    SearchLoadDurationClassesResponse,
    InsertLoadDurationClassBody,
    UpdateLoadDurationClassBody
  >(useApi(), SCREW_CONNECTOR_ADMIN_URL, "load_duration_classes", [SCREW_CONNECTOR_URL]);

  return {
    useLoadDurationClasses: crud.useSearch,
    insertLoadDurationClass: async (body: InsertLoadDurationClassBody): Promise<void> => {
      await crud.insert(body);
    },
    updateLoadDurationClass: (code: string, body: UpdateLoadDurationClassBody) =>
      crud.update(encodeURIComponent(code), body),
    deleteLoadDurationClass: (code: string) => crud.remove(encodeURIComponent(code)),
  };
};

// ─── Service classes: keyed by their number ───
export const useScrewConnectorServiceClassesApi = () => {
  const crud = createAdminCrudApi<SearchServiceClassesResponse, InsertServiceClassBody, UpdateServiceClassBody>(
    useApi(),
    SCREW_CONNECTOR_ADMIN_URL,
    "service_classes",
    [SCREW_CONNECTOR_URL],
  );

  return {
    useServiceClasses: crud.useSearch,
    insertServiceClass: async (body: InsertServiceClassBody): Promise<void> => {
      await crud.insert(body);
    },
    updateServiceClass: (code: number, body: UpdateServiceClassBody) => crud.update(`${code}`, body),
    deleteServiceClass: (code: number) => crud.remove(`${code}`),
  };
};

// ─── Kmod matrix: a 3 (service class) x N (load duration class) grid, edited as a single matrix ───
export const useScrewConnectorKmodValuesApi = () => {
  const crud = createAdminCrudApi<SearchKmodValuesResponse, UpsertKmodValueBody>(
    useApi(),
    SCREW_CONNECTOR_ADMIN_URL,
    "kmod_values",
    [SCREW_CONNECTOR_URL],
  );

  return {
    useKmodValues: crud.useSearch,
    // POST / is the backend's actual upsert (insert-or-update) endpoint — the matrix editor can
    // commit a cell whether or not a row already exists for that (serviceClassCode,
    // loadDurationClassCode) pair, unlike the id-scoped PUT which 404s if the row is missing.
    upsertKmodValue: async (body: UpsertKmodValueBody): Promise<void> => {
      await crud.insert(body);
    },
  };
};

// ─── Gamma M values: 2 fixed rows (EC5 / NTC2018), edited inline ───
export const useScrewConnectorGammaMValuesApi = () => {
  const crud = createAdminCrudApi<SearchGammaMValuesResponse, never, UpdateGammaMValueBody>(
    useApi(),
    SCREW_CONNECTOR_ADMIN_URL,
    "gamma_m_values",
    [SCREW_CONNECTOR_URL],
  );

  return {
    useGammaMValues: crud.useSearch,
    updateGammaMValue: (standard: GammaMValue["standard"], body: UpdateGammaMValueBody) =>
      crud.update(standard, body),
  };
};
